/**
 * What the map labels should read when the station filter is narrowing by sensor.
 *
 * Filtering to "water temperature" answers *where* it is measured but not *what
 * it says*, which is the actual question — you still had to open six stations
 * one at a time to compare them. With a metric selected the labels carry that
 * measure instead of wind speed, so the comparison is the map itself.
 *
 * The arrows keep meaning wind: same colour ramp, same direction. Colour on this
 * map has meant wind strength throughout, and re-pointing it at temperature
 * would need a different ramp and a different legend for a passing selection.
 * The label instead carries its unit, so a number that is not knots says so —
 * and so that two stacked values cannot be read as one.
 */

export type MetricKey = 'wind' | 'waterTemp' | 'airTemp' | 'waterLevel' | 'waves';

export interface MetricSpec {
  key: MetricKey;
  /** The station-filter key this corresponds to. */
  filterKey: string;
  label: string;
  unit: string;
  decimals: number;
}

export const METRICS: MetricSpec[] = [
  // Wind leads, and needs no fetch: every station already carries its reading.
  { key: 'wind', filterKey: 'wind', label: 'Wind', unit: 'kt', decimals: 0 },
  { key: 'waterTemp', filterKey: 'waterTemp', label: 'Water temperature', unit: '°F', decimals: 0 },
  { key: 'airTemp', filterKey: 'airTemp', label: 'Air temperature', unit: '°F', decimals: 0 },
  { key: 'waterLevel', filterKey: 'waterLevel', label: 'Water level', unit: 'ft', decimals: 1 },
  { key: 'waves', filterKey: 'waves', label: 'Wave height', unit: 'ft', decimals: 1 },
];

/**
 * Every measure to label the map with — empty means keep showing wind.
 *
 * All of them, not one: the filters are AND, so each metric added leaves fewer
 * stations to label. Two metrics leave at most six stations and usually three;
 * three leave at most three; all four leave none. The crowding a second value
 * costs is therefore always smaller than the crowding a single metric already
 * allows, since air temperature alone covers fifteen of the eighteen.
 *
 * The "Moored buoys" filter is a kind, not a measure, so it never appears here
 * and never suppresses a metric chosen alongside it.
 */
export function displayedMetrics(active: ReadonlySet<string>): MetricSpec[] {
  return METRICS.filter((m) => active.has(m.filterKey));
}

/**
 * Whether a national station can answer the selected filters.
 *
 * Judged on the values actually present rather than a declared sensor list,
 * because these arrive as one bulk row and there is nothing else to go on. A
 * station whose water temperature is absent from that row is excluded from a
 * water-temperature filter, which is the same promise the curated set makes.
 *
 * Water level has no national equivalent — moored buoys report no tidal datum —
 * so selecting it excludes them all, correctly.
 */
export function nationalMatchesFilters(
  active: ReadonlySet<string>,
  available: { waves: boolean; airTemp: boolean; waterTemp: boolean },
): boolean {
  if (active.size === 0) return true;
  if (active.has('waterLevel')) return false;
  // "Moored buoys" is a kind rather than a measure; the caller resolves it.
  if (active.has('waterTemp') && !available.waterTemp) return false;
  if (active.has('airTemp') && !available.airTemp) return false;
  if (active.has('waves') && !available.waves) return false;
  return true;
}

/** What a national station reported in the one bulk row we have for it. */
export interface NationalReading {
  /** Null on a wave buoy — CDIP hulls carry no anemometer. */
  speedKt: number | null;
  waveHeightM: number | null;
  airTempC: number | null;
  waterTempC: number | null;
  /** Registry kind, for the "moored buoys" filter. */
  type: string;
}

/**
 * Whether a national station belongs on the map right now.
 *
 * Two rules, and the second is the one that is easy to get wrong. A station
 * must satisfy the active filters, *and* it must have something to show for
 * whatever the labels are currently displaying. A wave buoy reports no wind, so
 * on a map showing wind it would be an empty marker among the arrows saying only
 * that a hull floats there — but under the waves filter it is the whole point.
 */
export function nationalVisible(
  active: ReadonlySet<string>,
  metrics: ReadonlyArray<MetricSpec>,
  s: NationalReading,
): boolean {
  if (active.has('buoy') && s.type !== 'buoy') return false;

  // Wind is what the labels carry when no other metric is selected.
  const showingOnlyWind = metrics.length === 0 || metrics.every((m) => m.key === 'wind');
  if (s.speedKt === null && showingOnlyWind) return false;

  return nationalMatchesFilters(active, {
    waves: s.waveHeightM !== null,
    airTemp: s.airTempC !== null,
    waterTemp: s.waterTempC !== null,
  });
}

/** The label text for a station, given the metric and its value. */
export function metricLabel(metric: MetricSpec, value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  // Unit travels with the number: on a map whose other numbers are knots, a
  // bare "55" beside an arrow would read as a gale.
  return `${value.toFixed(metric.decimals)}${metric.unit}`;
}
